import cn from 'classnames';
import { IconArrowDown } from 'components/Common/Icons';
import IconArrowUp from 'components/Common/Icons/IconArrowUp';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import updateSearchQueryStringOperation from 'store/search/operation/updateSearchQueryStringOperation';
import getSearchFilters from 'store/search/selector/getSearchFilters';
import isSearchLoading from 'store/search/selector/isSearchLoading';
import { setSearchPageNrAction, toggleSearchFilterAction } from 'store/search/slice';
import { SearchFilterFacet } from 'store/search/types';
import OpColorOption from 'components/Common/op-ui-wrappers/OpColorOption/OpColorOption';
import gtmSearchFilterEvent from 'utils/gtm/gtmSearchFilterEvent';
import gtmSendEvent from 'utils/gtm/gtmSendEvent';
import isDesktop from 'store/app/selector/isDesktop';
import CheckMark from './CheckMark';
import styles from './SearchFilters.module.scss';

enum FilterType {
  Color = 'designColors',
}

const FILTER_TO_HIDE = ['searchCategoryIds'];

interface SearchFiltersProps {
  showTitle?: boolean;
}

const SearchFilters: FC<SearchFiltersProps> = ({ showTitle = true }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const filters = useSelector(getSearchFilters);
  const isDesktopDevice = useSelector(isDesktop);
  const isLoadingResults = useSelector(isSearchLoading);
  const { t } = useTranslation();

  const [expandedSections, setExpandedSections] = useState<string[]>([]);

  useEffect(() => {
    // automatically expand all sections where any selected element
  }, []);

  const isSectionExpanded = (sectionId: string) => expandedSections.indexOf(sectionId) >= 0;

  const toggleFilter = (filterId: string, value: string | boolean, checked?: boolean) => {
    gtmSendEvent(gtmSearchFilterEvent(filterId, value, !checked));
    dispatch(toggleSearchFilterAction({ id: filterId, value }));
    dispatch(setSearchPageNrAction(1));
    isDesktopDevice && dispatch(updateSearchQueryStringOperation(history));
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((currentSet) => {
      const sectionIndexToExclude = currentSet.indexOf(sectionId);
      if (sectionIndexToExclude >= 0) {
        currentSet.splice(sectionIndexToExclude, 1);
      } else {
        currentSet.push(sectionId);
      }
      return [...currentSet];
    });
  };

  const renderColorFilter = (facets: SearchFilterFacet[], filterId: string) => {
    return (
      <div className={styles.colors}>
        {facets.map((facet) => {
          return (
            <div key={facet.name} className={styles.colorOption}>
              <OpColorOption
                key={facet.name}
                size="small"
                color={facet.icon || ''}
                selected={facet.checked}
                label={facet.name}
                className={styles.color}
                textVariant="body-large-regular"
                showLabel
                onClick={() => toggleFilter(filterId, facet.value, facet.checked)}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderFilter = (facets: SearchFilterFacet[], filterId: string, name: string) => {
    if (filterId === FilterType.Color) {
      return renderColorFilter(facets, filterId);
    }

    return facets.map((facet) => {
      const isExpandable = Boolean(facet.subSections);
      const isExpanded = isSectionExpanded(facet.name);

      if (isExpandable) {
        return (
          <div key={facet.name} className={cn(styles.section, { [styles.expanded]: isExpanded })}>
            <div className={cn(styles.sectionTitle, { [styles.disabled]: facet.count === 0 })}>
              <div className={styles.checkmark} onClick={() => toggleFilter(filterId, facet.value, facet.checked)}>
                <CheckMark checked={Boolean(facet.checked)} />
              </div>
              <OpText
                variant="body"
                size="large"
                onClick={() => toggleSection(facet.name)}
                className={cn(styles.title, { [styles.disabled]: facet.count === 0 })}
              >
                {facet.name} ({facet.count})
              </OpText>
              <div onClick={() => toggleSection(facet.name)}>
                {isExpanded && <IconArrowUp className={styles.icon} />}
                {!isExpanded && <IconArrowDown className={styles.icon} />}
              </div>
            </div>
            {facet.subSections && (
              <div className={cn(styles.subSection, { [styles.expanded]: isExpanded })}>
                {renderFilter(facet.subSections, filterId, name)}
              </div>
            )}
          </div>
        );
      }
      // skip empty facets values
      if (!facet.name || (!facet.value && facet.value !== false)) return null;
      return (
        <div key={facet.name} className={cn(styles.sectionTitle, { [styles.disabled]: facet.count === 0 })}>
          <div className={styles.checkmark} onClick={() => toggleFilter(filterId, facet.value, facet.checked)}>
            <CheckMark checked={Boolean(facet.checked)} />
          </div>
          <OpText
            variant="body"
            size="large"
            onClick={() => toggleFilter(filterId, facet.value, facet.checked)}
            className={styles.title}
          >
            {facet.name} ({facet.count})
          </OpText>
        </div>
      );
    });
  };

  return (
    <aside className={cn(styles.searchFilters, { [styles.busy]: isLoadingResults })}>
      {showTitle && (
        <OpText variant="title" size="large" className={styles.heading}>
          {t('txt_SearchFilters')}
        </OpText>
      )}

      {filters
        .filter((f) => !FILTER_TO_HIDE.includes(f.id))
        .map((filter, index) => (
          <div key={`f${index}`} className={styles.filterGroup}>
            <OpText variant="body-large-regular">{filter.name}</OpText>
            {renderFilter(filter.facets, filter.id, filter.name)}
          </div>
        ))}
    </aside>
  );
};

export default SearchFilters;
